import { ConsentType, PaymentProvider } from './payment';

import { CurrentCompanyResponse } from '@/services/PaymentsServiceV2';
import {
    CompanyResponse,
    LoginType as SharedLoginType,
    Theme as SharedTheme
} from '@instapaid/shared-logic';

export type CompanyAccount = {
    companyId: string;
    name: string;
    balance: number;
    pendingTransactionsCount: number;
    currency: string;
};

export type CompanyAccountsResponse = {
    companyAccounts: CompanyAccount[];
};

export interface Company extends CompanyResponse {}
export interface Theme extends SharedTheme {}

export enum LoginType {
    AzureB2C = 'AzureB2C',
    AzureAD = 'AzureAD'
}

export type CompanyWithAdmins = CompanyResponse & {
    admins: AdminResponse[];
};

export const paymentSources = [
    'CompanyAccount',
    'InstaPaidAccount',
    'InstaPaidEurAccount'
] as const;
export type PaymentSource = (typeof paymentSources)[number];

export const approvalTypes = ['Manual', 'Automatic'] as const;
export type ApprovalType = (typeof approvalTypes)[number];

export const lowBalanceAudiences = [
    'None',
    'AllAdmins',
    'PendingWithdrawalsEmails',
    'Custom'
] as const;
export type LowBalanceAudience = (typeof lowBalanceAudiences)[number];

export const getLowBalanceAudienceLabel = (
    lowBalanceAudience: LowBalanceAudience
) => {
    switch (lowBalanceAudience) {
        case 'AllAdmins':
            return 'All company admins';
        case 'Custom':
            return 'Custom list of email addresses';
        case 'PendingWithdrawalsEmails':
            return 'The same email which is notified about pending withdrawals';
        default:
            return lowBalanceAudience;
    }
};

export type CompaniesResponse = {
    companies: CompanyWithAdmins[];
};

export type User = {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
};

export type AdminResponse = User & {
    id: string;
    isActive: boolean;
};

export enum UserRoles {
    User = 'User',
    SuperAdmin = 'SuperAdmin',
    Admin = 'Admin',
    Manager = 'Manager',
    GroupAdmin = 'GroupAdmin'
}

export type ResetUsersResponse = {
    disabledUsers: User[];
    resetUsers: User[];
};

export type UpdateUserRoleRequest = {
    userId: string;
    role: UserRoles;
};

export type CurrentUser = User & {
    id: string;
    role: UserRoles;
    isGlobalAdmin?: boolean;
};

/**
 * Company-agnostic identity, resolvable before a company is selected.
 * Only the platform-wide SuperAdmin role is knowable here; per-company roles
 * come from getCurrentUser once a company is picked.
 */
export type UserIdentity = {
    id: string;
    email: string;
    role: UserRoles | null;
    isGlobalAdmin: boolean;
};

export type UserResponse = User & {
    id: string;
    isActive: boolean;
    isDisabled: boolean;
    role: UserRoles;
};

export type CompanyWithUsers = Company & {
    users: Array<UserResponse>;
};

export type UploadCompanyLogoRequest = {
    formData: FormData;
};

export type UpdateCompanyRequest = {
    greetingText: string;
    theme: string;
    defaultLanguage: string;
};

export type ActionType = 'Accepted' | 'Declined' | 'NotSigned' | 'Postponed';

export type ConsentResponse = {
    consentId: string;
    consentType: ConsentType;
    companyId: string;
    performedByUserId: string;
    performedByEmail?: string;
    performedByName: string;
    performedAt: Date;
    actionType: ActionType;
    termsVersionId: string;
    termsVersionName: string | null;
    termsVersionUrl: string | null;
};

export type GetConsentsResponse = {
    consents: ConsentResponse[];
};

export type UpdateConsentsRequest = {
    commercialConsented?: boolean;
    dataConsented?: boolean;
    newsletterAndUpdatesConsented?: boolean | null;
    commercialTermsVersion?: string | null;
    dataTermsVersion?: string | null;
};

export type UpdateCompanyAuthSettingsRequest = {
    tenantId?: string;
    loginType?: LoginType;
};

export type CreateCompanyRequest = {
    name: string;
    currency: string;
    country: string;
    tenantId: string;
    loginType: LoginType;
    salaryProvider: string;
    timeRegistrationProvider: string;
    pricePerUser: number;
    employeeWithdrawalFee: number;
    companyServiceFee: number;
    endOfTrial: Date;
    paymentProvider: PaymentProvider;
    paymentSource: PaymentSource;
    approvalType: ApprovalType;
    lowBalanceNotificationThreshold: number | null;
    lowBalanceAudience: LowBalanceAudience;
    lowBalanceEmail: string | null;
    admin: User;
};

export type CreateUserRequest = User & {
    isActive: boolean;
    role: UserRoles;
};

export type UpdateUserRequest = {
    id: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
};

export type ChangeUsersStatusRequest = {
    userIds: string[];
};

export type Language = {
    code: string;
    name: string;
};

export type LanguagesResponse = {
    languages: Language[];
};

export interface CompanySettingProps {
    companyDetails?: { data: CurrentCompanyResponse };
    currentUser?: any;
    flag?: boolean;
    reloadDetails?: () => void;
}

export type UserRolesType = {
    id: string;
    name: UserRoles;
    isUsersSyncEnabled: boolean;
};

export type UserRolesResponse = {
    userRole: UserRolesType[];
};

export type ActionsType = 'add' | 'delete';

export type AdminCompanyIds = {
    companyId: string;
    action: string;
};

export type CreateUsersInMultiCompaniesRequest = {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    isActive: boolean;
    role: string;
    companies: AdminCompanyIds[];
};
